/**
 * PRP-FEEDBACK-1 W1 (founder ruling R4): "Overhead X% / Profit Y%" in the estimate meta grid,
 * with its source wording, editable inline. Saving writes the two metadata fields through
 * PATCH /imports/{hash}/metadata; the next export's PARAMS carry them.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import {
  getImportOpRates,
  updateImportOpRates,
  OP_SOURCE_LABEL,
  type OpRates,
} from '../../lib/importMetadataApi';
import { Button } from './ui/button';
import { Input } from './ui/input';

const RANGE = 'Each percentage must be a number from 0 to 100, with up to two decimals.';

/** `10` -> "10", `12.5` -> "12.5": the way Xactimate's PARAMS carry a rate. */
export function formatPct(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

function readPct(raw: string): number | null {
  const text = raw.trim().replace(/%$/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const n = Number(text);
  return n >= 0 && n <= 100 ? n : null;
}

interface Props {
  parseHash: string;
}

export default function OpRatesField({ parseHash }: Props) {
  const [rates, setRates] = useState<OpRates | null>(null);
  const [load, setLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [editing, setEditing] = useState(false);
  const [overhead, setOverhead] = useState('');
  const [profit, setProfit] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'bad'; msg: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoad('loading');
    setEditing(false);
    getImportOpRates(parseHash)
      .then((r) => {
        if (cancelled) return;
        setRates(r);
        setLoad('ready');
      })
      .catch(() => {
        if (!cancelled) setLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [parseHash]);

  function startEdit() {
    if (!rates) return;
    setOverhead(formatPct(rates.overhead_pct));
    setProfit(formatPct(rates.profit_pct));
    setFlash(null);
    setEditing(true);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const oh = readPct(overhead);
    const pf = readPct(profit);
    if (oh === null || pf === null) {
      setFlash({ kind: 'bad', msg: RANGE });
      return;
    }
    setBusy(true);
    setFlash(null);
    try {
      const out = await updateImportOpRates(parseHash, { overhead_pct: oh, profit_pct: pf });
      setRates(out);
      setEditing(false);
      setFlash({ kind: 'ok', msg: 'Saved. The next export carries these percentages.' });
    } catch (err: unknown) {
      const status = (err as { status?: number } | null)?.status;
      setFlash({
        kind: 'bad',
        msg:
          status === 409
            ? 'The estimate changed while you were editing. Reload and try again.'
            : 'Could not save. The percentages are unchanged.',
      });
    } finally {
      setBusy(false);
    }
  }

  const label = 'Overhead / Profit';
  if (load === 'loading') {
    return (
      <div data-testid="op-rates">
        <div className="text-[10.5px] text-[var(--tx-lo)] mb-0.5">{label}</div>
        <div className="text-[var(--tx-lo)]">…</div>
      </div>
    );
  }
  if (load === 'failed' || !rates) {
    return (
      <div data-testid="op-rates">
        <div className="text-[10.5px] text-[var(--tx-lo)] mb-0.5">{label}</div>
        <div className="text-[var(--tx-lo)]">Not available</div>
      </div>
    );
  }

  const summary = `Overhead ${formatPct(rates.overhead_pct)}% / Profit ${formatPct(rates.profit_pct)}%`;
  return (
    <div data-testid="op-rates" className="min-w-0">
      <div className="text-[10.5px] text-[var(--tx-lo)] mb-0.5">{label}</div>
      {!editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[var(--tx-hi)] whitespace-nowrap">{summary}</span>
          <span className="text-[11px] text-[var(--tx-faint)] whitespace-nowrap">
            ({OP_SOURCE_LABEL[rates.op_source] ?? rates.op_source})
          </span>
          <Button size="sm" variant="ghost" type="button" onClick={startEdit} aria-label="Edit overhead and profit">
            Edit
          </Button>
        </div>
      ) : (
        <form className="flex items-center gap-[6px] flex-wrap" onSubmit={onSave} aria-label="Overhead and profit">
          <label className="flex items-center gap-1 text-[11.5px] text-[var(--tx-lo)]">
            Overhead
            <Input
              aria-label="Overhead %"
              inputMode="decimal"
              className="w-[64px] h-[28px] px-[7px]"
              value={overhead}
              onChange={(e) => setOverhead(e.target.value)}
              disabled={busy}
            />
            %
          </label>
          <label className="flex items-center gap-1 text-[11.5px] text-[var(--tx-lo)]">
            Profit
            <Input
              aria-label="Profit %"
              inputMode="decimal"
              className="w-[64px] h-[28px] px-[7px]"
              value={profit}
              onChange={(e) => setProfit(e.target.value)}
              disabled={busy}
            />
            %
          </label>
          <Button size="sm" variant="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
        </form>
      )}
      {flash && (
        <p
          role="status"
          data-testid="op-rates-flash"
          className={`m-0 mt-[2px] text-[11.5px] ${flash.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}
        >
          {flash.msg}
        </p>
      )}
    </div>
  );
}
